/**
 * Global-search keyboard shortcuts.
 *
 * ⌘K / Ctrl+K focuses the search from anywhere. "/" also focuses, but only
 * when the user isn't already typing in another input / textarea / editable
 * element — otherwise it eats slashes inside forms.
 *
 * Split out of `global-search.tsx` so the main component fits under the
 * project's file-length threshold.
 */

import { RefObject, useEffect } from 'react';

export function useGlobalSearchShortcuts(
  inputRef: RefObject<HTMLInputElement | null>,
  open: () => void
): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const shortcut = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (shortcut) {
        e.preventDefault();
        inputRef.current?.focus();
        open();
        return;
      }
      if (e.key === '/') {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target?.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          inputRef.current?.focus();
          open();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // inputRef identity is stable; `open` is a fresh reference each render
    // but the effect only reads it via the closure, so we intentionally omit
    // it from deps to avoid re-attaching the window listener every render.
  }, []);
}
